// src/components/frontpage/columns/GroupHomeColumn.jsx
//
// Group-home Mode Column — column 1 of the Group desktop front page (Groups Tier 1/2).
// The mauve-context sibling of DirectoryColumn. As of Groups Tier 2 Commit 2 it is a thin
// SHELL only: it mounts the shared ModeColumn chrome (header suppressed — the identity block IS
// the column's identity; pinned footer holding the federation membership statement (when
// federated) above the branding; membership modals in the `overlay` slot) and delegates its
// entire body to the shared <GroupHomePanel/>. The mobile Home tab mounts the SAME panel bare.
// One source of truth for the home content — a change to the panel appears on both the rail and
// the mobile tab automatically. The federation membership statement is the one exception to
// "same content everywhere": it renders from this pinned footer slot on desktop and from
// MobileGroupFrontPage's Home-tab case on mobile — never from GroupHomePanel itself, since
// GroupHomePanel's own scrollable body can't stay reliably pinned above a footer whose content
// height varies (see FederationMembershipStatement.jsx).
//
//   ModeColumn shell  (pinned footer: federation statement + branding · membership modals overlay)
//     └── GroupHomePanel  (identity · membership row · sub-groups · federation members · part-of)
//
// All membership + hierarchy data/actions come from a single useGroupHome instance owned here:
// the panel consumes it for its content + buttons; this shell consumes the modal flags for the
// overlay. No double fetch, no double-mounted modals.
//
// Scope notes (unchanged):
//   - Sub-group rows SELECT the child via onGroupSelect (the §4-gated handleViewGroup path).
//   - Members roster SUPPRESSED (privacy-by-construction): the hook gates the fetch via
//     showMembers (default false); the aggregate meta headcount stays (non-sensitive).

import React from 'react';
import { useTheme } from '../../../themes/ThemeProvider';
import { useTranslation } from '../../../i18n/useTranslation';
import ModeColumn from './ModeColumn';
import GroupHomePanel from '../../groups/GroupHomePanel';
import FederationMembershipStatement from '../../groups/FederationMembershipStatement';
import JoinRequestModal from '../../groups/JoinRequestModal';
import FederationApplyModal from '../../groups/FederationApplyModal';
import ShareLinkModal from '../../core/ShareLinkModal';
import ConfirmationModal from '../../core/ConfirmationModal';
import { useGroupHome } from '../../../hooks/useGroupHome';

const GroupHomeColumn = ({
  group,
  currentUser,
  onGroupSelect,
  onMembershipChange,
  hideColumnHeader = true
}) => {
  const theme = useTheme();
  const { t } = useTranslation('groups');
  const mauve = theme?.primary || '#BB4D9A';

  // Single hook instance for the column — the panel gets the whole `home` object; this shell
  // only needs the modal flags/handlers for its overlay slot.
  const home = useGroupHome(group, currentUser, { onMembershipChange });
  const {
    handleJoinGroup,
    confirmLeaveGroup,
    showJoinModal,
    setShowJoinModal,
    joinLoading,
    showShareModal,
    setShowShareModal,
    showLeaveConfirmation,
    setShowLeaveConfirmation,
    federationApplyEligibility,
    submitFederationApplication,
    showFederationApplyModal,
    setShowFederationApplyModal,
    federationApplyLoading,
    federationGroup
  } = home;

  if (!group) return null;

  return (
    <ModeColumn
      title={group.name}
      themeColor={mauve}
      hideColumnHeader={hideColumnHeader}
      footer={
        <div style={{ borderTop: '1px solid #eceff4' }}>
          {/* Federation membership statement — pinned, above the branding row, still part of
              the one non-scrolling footer slot. Only GroupHomeColumn (desktop) and
              MobileGroupFrontPage (mobile Home tab) ever render this; GroupHomePanel itself
              never does, so there is no double-render to manage (Michael's placement ruling,
              13 Sep — see FederationMembershipStatement.jsx for the full reasoning). The border
              above lives on this outer wrapper, not the branding row below, so it still marks
              "pinned chrome begins here" even with the statement present. */}
          {federationGroup && (
            <FederationMembershipStatement
              federationGroup={federationGroup}
              onGroupSelect={onGroupSelect}
            />
          )}
          <div style={{
            paddingTop: '12px', paddingBottom: '4px',
            display: 'flex', alignItems: 'center', gap: '12px'
          }}>
            <img
              src="/images/logos/logo-ournet.png"
              alt="OurNet Logo"
              style={{ width: '48px', height: '47px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: mauve, letterSpacing: '0.5px' }}>
              {t('ui:tagline')}
            </div>
          </div>
        </div>
      }
      overlay={
        <>
          {showJoinModal && (
            <JoinRequestModal
              isOpen={showJoinModal}
              onClose={() => setShowJoinModal(false)}
              onSubmit={handleJoinGroup}
              groupName={group.name}
              loading={joinLoading}
            />
          )}
          <ShareLinkModal
            visible={showShareModal}
            targetType="community"
            targetId={group.id}
            targetName={group.name}
            currentUser={currentUser}
            onClose={() => setShowShareModal(false)}
          />
          <ConfirmationModal
            visible={showLeaveConfirmation}
            title={`${t('ui:leave')}?`}
            message={`${group.name}`}
            onOk={confirmLeaveGroup}
            onCancel={() => setShowLeaveConfirmation(false)}
            okLabel={t('ui:leave')}
            cancelLabel={t('common:cancel')}
          />
          {showFederationApplyModal && (
            <FederationApplyModal
              isOpen={showFederationApplyModal}
              onClose={() => setShowFederationApplyModal(false)}
              onSubmit={submitFederationApplication}
              federationGroupId={group.id}
              federationName={group.name}
              applicantGroups={federationApplyEligibility?.applicableGroups || []}
              loading={federationApplyLoading}
            />
          )}
        </>
      }
    >
      <GroupHomePanel
        group={home.completeGroup}
        home={home}
        onGroupSelect={onGroupSelect}
      />
    </ModeColumn>
  );
};

export default GroupHomeColumn;
